import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { Drawer, Box, Typography, IconButton, Divider } from '@mui/material';
import { X } from 'lucide-react';
import {
    formatNotificationTime,
    formatNotificationText,
    getInitialsFromText,
    getNotificationAction,
    isAccessNotification
} from 'utils/helpers/notifications.helper';
import { useDispatch, useSelector } from 'react-redux';
import { useNotificationAction } from 'hooks/useNotificationAction';
import NotifcationItem from './NotifciationItem';
import KeyIcon from 'assets/icons/KeyIcon';
import AlertHexagonIcon from 'assets/icons/AlertHexagonIcon';
import SageSLogoIcon from 'assets/icons/SageSLogoIcon';
import StorefrontIcon from 'assets/icons/StoreFrontIcon';

function InitialsAvatar({ initials, bg = '#ecf1f3' }) {
    return (
        <Box
            sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                bgcolor: bg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
            }}
        >
            <Typography sx={{ fontSize: '11px', fontWeight: 500, color: '#596470' }}>
                {initials}
            </Typography>
        </Box>
    );
}

function IconAvatar({ bg, children }) {
    return (
        <Box
            sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                bgcolor: bg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
            }}
        >
            {children}
        </Box>
    );
}

function getNotificationAvatar(notification) {
    if (notification?.template_key === 'otc-consumed') {
        return (
            <IconAvatar bg="#ecf1f3">
                <KeyIcon size={18} color="#596470" />
            </IconAvatar>
        );
    }
    if (
        notification?.template_key === 'access-key-suspended' ||
        notification?.template_key === 'vehicle-verification-denied' ||
        notification?.template_key === 'lpr-license-denied'
    ) {
        return (
            <IconAvatar bg="#FFE7E8">
                <AlertHexagonIcon size={18} />
            </IconAvatar>
        );
    }
    if (
        notification?.template_key === 'access-key-created' ||
        notification?.template_key === 'invite-access' ||
        notification?.template_key === 'access-key-updated' ||
        notification?.template_key === 'vehicle-verification-approved' ||
        notification?.template_key === 'lpr-license-approved' ||
        notification?.template_key === 'invite-user'
    ) {
        return (
            <IconAvatar bg="#D5F9DD">
                <SageSLogoIcon size={18} />
            </IconAvatar>
        );
    }
    if (notification?.template_key === 'access-granted-vendor') {
        return (
            <IconAvatar bg="#E3F7F3">
                <StorefrontIcon size={18} />
            </IconAvatar>
        );
    }
    if (notification?.template_key === 'credential-grants-provisioned') {
        return (
            <IconAvatar bg="#D5F9DD">
                <SageSLogoIcon size={18} />
            </IconAvatar>
        );
    }
    if (
        notification?.template_key === 'credential-grants-pending' ||
        notification?.template_key === 'invite-pending-approval'
    ) {
        return (
            <IconAvatar bg="#FFE7E8">
                <AlertHexagonIcon size={18} />
            </IconAvatar>
        );
    }
    if (
        notification?.template_key === 'credential-grants-holder-removed' ||
        notification?.template_key === 'credential-grants-access-ended'
    ) {
        return (
            <IconAvatar bg="#FFE7E8">
                <AlertHexagonIcon size={18} />
            </IconAvatar>
        );
    }
    if (notification?.source_type === 'incident_report') {
        return (
            <IconAvatar bg="#FFE7E8">
                <AlertHexagonIcon size={18} />
            </IconAvatar>
        );
    }

    return <InitialsAvatar initials={getInitialsFromText(notification?.text)} />;
}

const TABS = ['all', 'access'];

// `headerOffset` must match the height of the AppBar this drawer opens beneath: the resi
// TopNavigation pins its Toolbar to 64px, while MainLayout's uses the theme default
// (`mixins.toolbar.minHeight`, currently 60). Passing the wrong one leaves a strip of page
// content showing above the drawer, which `hideBackdrop` makes obvious.
export function NotificationsDrawer({
    open,
    onClose,
    items,
    showTabs = true,
    showOrgName = false,
    headerOffset = 64
}) {
    const [activeTab, setActiveTab] = useState('all');
    const [newNotificationId, setNewNotificationId] = useState(null);
    const previousIdsRef = useRef([]);
    const [openedUnreadIds, setOpenedUnreadIds] = useState(new Set());

    useEffect(() => {
        if (open) {
            setOpenedUnreadIds(
                new Set(items.filter(item => item.unread).map(item => item.id))
            );
        } else {
            setOpenedUnreadIds(new Set());
        }
        // we disable the dependency for items in this effect because we don't want the effect to run
        // every time that the API data changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        if (!open) return;

        setOpenedUnreadIds(prev => {
            const next = new Set(prev);

            items.forEach(item => {
                if (item.unread) {
                    next.add(item.id);
                }
            });

            return next;
        });
    }, [items, open]);

    const notifications = useMemo(() => {
        return items.map(notification => ({
            id: notification.id,
            avatar: getNotificationAvatar(notification),
            text: formatNotificationText(notification),
            time: formatNotificationTime(notification.created_at),
            unread: openedUnreadIds.has(notification.id),
            tab: isAccessNotification(notification) ? 'access' : 'all',
            orgName: showOrgName ? notification.org_name : null,
            action: getNotificationAction(notification)
        }));
    }, [items, openedUnreadIds, showOrgName]);

    const visible =
        !showTabs || activeTab === 'all'
            ? notifications
            : notifications.filter(notification => notification.tab === 'access');

    useEffect(() => {
        const currentIds = items.map(n => n.id);

        if (previousIdsRef.current.length === 0) {
            previousIdsRef.current = currentIds;
            return;
        }

        const newId = currentIds.find(id => !previousIdsRef.current.includes(id));

        if (newId) {
            setNewNotificationId(newId);

            setTimeout(() => {
                setNewNotificationId(null);
            }, 450);
        }

        previousIdsRef.current = currentIds;
    }, [items]);

    const dispatch = useDispatch();

    // Acting on a notification takes you to the page that can actually resolve it, and closes the
    // drawer behind you — leaving it open over the destination hides the thing you came to look at.
    // Notifications are uid-scoped across every org a manager administers, and the page is the
    // operator app's, so the action switches to its org (and app) first (`useNotificationAction`).
    const { openAction, switching } = useNotificationAction({ onDone: onClose });

    const hasMore = useSelector(state => state.notifications.hasMore);
    const isLoadingMore = useSelector(state => state.notifications.isLoadingMore);

    const loader = useRef(null);
    const scrollContainerRef = useRef(null);

    const handleScroll = useCallback(
        event => {
            const element = event.currentTarget;

            const nearBottom =
                element.scrollHeight - element.scrollTop <= element.clientHeight + 100;

            if (nearBottom && hasMore && !isLoadingMore) {
                dispatch({ type: 'LOAD_MORE_NOTIFICATIONS' });
            }
        },
        [dispatch, hasMore, isLoadingMore]
    );

    useEffect(() => {
        if (!open) return;
        if (openedUnreadIds.size === 0) return;

        const timeout = setTimeout(() => {
            setOpenedUnreadIds(new Set());
        }, 4000);

        return () => clearTimeout(timeout);
    }, [open, openedUnreadIds]);
    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            hideBackdrop
            PaperProps={{
                sx: {
                    width: 330,
                    borderRadius: '12px 0 0 12px',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    border: '1px solid #f0f0f0',
                    borderRight: 'none',
                    boxShadow: '0px 0px 8px 0px rgba(0,0,0,0.15)',
                    top: `${headerOffset}px`,
                    height: `calc(100vh - ${headerOffset}px)`
                }
            }}
            ModalProps={{
                keepMounted: true,
                disableAutoFocus: true,
                disableEnforceFocus: true,
                disableRestoreFocus: true
            }}
            sx={{ pointerEvents: 'none', '& .MuiPaper-root': { pointerEvents: 'auto' } }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    pl: '20px',
                    pr: '15px',
                    height: 42,
                    flexShrink: 0
                }}
            >
                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#212322' }}>
                    Notifications
                </Typography>

                <IconButton
                    size="small"
                    onClick={onClose}
                    sx={{ color: '#262626', p: '4px' }}
                >
                    <X size={16} strokeWidth={1.5} />
                </IconButton>
            </Box>

            <Divider sx={{ borderColor: '#f0f0f0', flexShrink: 0 }} />

            {showTabs && (
                <>
                    <Box
                        sx={{
                            bgcolor: '#f8f8f8',
                            height: 40,
                            display: 'flex',
                            alignItems: 'flex-end',
                            px: '25px',
                            flexShrink: 0
                        }}
                    >
                        {TABS.map(tab => (
                            <Box
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    mr: tab === 'all' ? '32px' : 0,
                                    cursor: 'pointer',
                                    height: '100%',
                                    justifyContent: 'space-between',
                                    pt: '10px'
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: '13px',
                                        color: activeTab === tab ? '#33c78f' : '#262626',
                                        lineHeight: '22px',
                                        userSelect: 'none'
                                    }}
                                >
                                    {tab === 'all' ? 'All' : 'Access log'}
                                </Typography>

                                <Box
                                    sx={{
                                        height: 2,
                                        width: '100%',
                                        bgcolor:
                                            activeTab === tab ? '#33c78f' : 'transparent',
                                        borderRadius: '1px'
                                    }}
                                />
                            </Box>
                        ))}
                    </Box>

                    <Divider sx={{ borderColor: '#f0f0f0', flexShrink: 0 }} />
                </>
            )}

            <Box
                ref={scrollContainerRef}
                onScroll={handleScroll}
                sx={{
                    overflowY: 'auto',
                    flex: 1,
                    minHeight: 0
                }}
            >
                {' '}
                {visible.map((notification, idx) => {
                    const isNew = notification.id === newNotificationId;
                    const isLastItem = idx < visible.length - 1;
                    return (
                        <NotifcationItem
                            notification={notification}
                            key={notification.id}
                            isNew={isNew}
                            isLastItem={isLastItem}
                            onAction={openAction}
                            actionDisabled={switching}
                        />
                    );
                })}
                <Box
                    ref={loader}
                    sx={{
                        py: 2,
                        textAlign: 'center'
                    }}
                >
                    <Typography variant="caption">
                        {isLoadingMore
                            ? 'Loading more notifications...'
                            : hasMore
                            ? 'Scroll for more'
                            : 'No more notifications'}
                    </Typography>
                </Box>
            </Box>
        </Drawer>
    );
}
